'use client';

import Cookies from 'js-cookie';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
   getConsentFromCookie,
   getPiwikConsentFromCookie,
   setConsentCookie,
} from './consent-storage';
import { CONSENT_ACCEPTED, CONSENT_PENDING, CONSENT_REJECTED, ConsentValue } from './consent-types';
import { trackCustomGAEvent } from '@ifixit/analytics';

export function useConsentCookie(piwikSiteId: string) {
   const [isReady, setIsReady] = useState(false);
   const [consentDecision, setConsentDecision] = useState<ConsentValue>(CONSENT_PENDING);

   useEffect(() => {
      let cancelled = false;

      const consent = getConsentFromCookie();
      if (consent) {
         // eslint-disable-next-line react-hooks/set-state-in-effect
         setConsentDecision(consent.isConsented);
         Cookies.remove(`ppms_privacy_${piwikSiteId}`);
      } else {
         // TODO: Remove Piwik fallback after all Piwik cookies expire (~Mar 2027)
         const piwikConsent = getPiwikConsentFromCookie(piwikSiteId);
         if (piwikConsent === true) {
            setConsentDecision(CONSENT_ACCEPTED);
            setConsentCookie(CONSENT_ACCEPTED);
         } else if (piwikConsent === false) {
            setConsentDecision(CONSENT_REJECTED);
            setConsentCookie(CONSENT_REJECTED);
         } else {
            if (cancelled) {
               return;
            }
            trackCustomGAEvent({
               eventAction: 'Consent Banner Impression',
               eventCategory: 'Consent',
            });
            setConsentDecision(CONSENT_PENDING);
            setConsentCookie(CONSENT_PENDING);
         }
      }
      if (cancelled) {
         return;
      }
      setIsReady(true);

      return () => {
         cancelled = true;
      };
   }, [piwikSiteId]);

   const acceptAll = useCallback(async () => {
      setConsentCookie(CONSENT_ACCEPTED);
      setConsentDecision(CONSENT_ACCEPTED);
   }, []);

   const rejectAll = useCallback(async () => {
      setConsentCookie(CONSENT_REJECTED);
      setConsentDecision(CONSENT_REJECTED);
   }, []);

   return useMemo(
      () => ({ isReady, consentDecision, acceptAll, rejectAll }),
      [isReady, consentDecision, acceptAll, rejectAll]
   );
}
